import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import { getFileUrl } from '@/utils/fileUrl';
import DocumentTypesPanel from './components/DocumentTypesPanel';

interface CompanySettings {
  id: number;
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
}

export default function Settings() {
  const { user } = useAuth();
  const { data: settings, loading, error, refetch } = useApiQuery<CompanySettings>({ endpoint: '/settings' });
  const { put, request } = useApi();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<CompanySettings>>();
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use logged-in user's company as the reliable identifier
  const companyId = user?.company?.id;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSaved(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const payload: Record<string, unknown> = { ...(form || {}) };
      if (Object.keys(payload).length === 0) {
        setSaveError('No changes to save');
        setSaving(false);
        return;
      }
      await put('/settings', payload);
      setSaved(true);
      refetch();
      setForm(undefined);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!companyId) {
      setLogoError('Company ID not available. Please log in again.');
      return;
    }

    setLogoUploading(true);
    setLogoError(null);

    try {
      const uploadForm = new FormData();
      uploadForm.append('logo', file);

      const res = await request(`/companies/${companyId}/logo`, {
        method: 'PUT',
        body: uploadForm,
      });

      setForm((prev) => ({ ...prev, logoUrl: res.data.logo_url }));
      setSaved(true);
      refetch();
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Logo upload failed');
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const current = { ...settings, ...form };

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900">Company Settings</h1>
        <p className="text-stone-500 text-sm mt-1">Manage your company profile, branding, and preferences.</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {saved && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-emerald-500" />
          <span className="text-emerald-700 text-sm">Settings saved successfully.</span>
        </div>
      )}

      {saveError && (
        <div className="mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <span className="text-red-700 text-sm">{saveError}</span>
        </div>
      )}

      {logoError && (
        <div className="mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <span className="text-red-700 text-sm">{logoError}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Branding */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">Company Profile</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Company Name</label>
                <input
                  type="text"
                  name="name"
                  value={current.name || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={current.slug || ''}
                  disabled
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-500 bg-stone-50 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Contact Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={current.contactEmail || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Contact Phone</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={current.contactPhone || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Address</label>
                <input
                  type="text"
                  name="address"
                  value={current.address || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">City</label>
                <input
                  type="text"
                  name="city"
                  value={current.city || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">State</label>
                <input
                  type="text"
                  name="state"
                  value={current.state || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Country</label>
                <input
                  type="text"
                  name="country"
                  value={current.country || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Postal Code</label>
                <input
                  type="text"
                  name="postalCode"
                  value={current.postalCode || ''}
                  onChange={handleChange}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">Theme Colors</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="primaryColor"
                    value={current.primaryColor || '#d97706'}
                    onChange={handleChange}
                    className="w-10 h-10 rounded-lg border border-stone-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    name="primaryColor"
                    value={current.primaryColor || ''}
                    onChange={handleChange}
                    className="flex-1 border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="secondaryColor"
                    value={current.secondaryColor || '#f59e0b'}
                    onChange={handleChange}
                    className="w-10 h-10 rounded-lg border border-stone-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    name="secondaryColor"
                    value={current.secondaryColor || ''}
                    onChange={handleChange}
                    className="flex-1 border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Document Types */}
          <DocumentTypesPanel canManage={user?.role === 'superadmin' || user?.role === 'company_admin'} />
        </div>

        {/* Right Panel */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">Company Logo</h3>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              className="hidden"
              onChange={handleLogoUpload}
            />
            <div
              onClick={handleLogoClick}
              className="w-full aspect-square bg-stone-50 rounded-xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center gap-2 mb-4 cursor-pointer hover:bg-stone-100 transition-colors relative overflow-hidden"
            >
              {logoUploading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <i className="ri-loader-4-line animate-spin text-brand-600 text-2xl" />
                </div>
              )}
              {current.logoUrl ? (
                <img src={getFileUrl(current.logoUrl)} alt="Logo" className="w-full h-full object-contain p-4" />
              ) : (
                <>
                  <i className="ri-image-line text-stone-300 text-3xl" />
                  <span className="text-stone-400 text-xs">Click to upload logo</span>
                </>
              )}
            </div>
            <p className="text-stone-400 text-xs">Recommended: 400x400px, PNG or JPG</p>
          </div>

          {/* Save */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold py-3 rounded-full text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Saving...
                </>
              ) : (
                <>Save Changes</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
